/* eslint-disable react/no-multi-comp */
import Fade from "@mui/material/Fade";
import { useDeepCompareMemo } from "@react-hookz/web";
import first from "lodash/first";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SwitchTransition } from "react-transition-group";
import { useLatest } from "react-use";
import { useSendImpressionEvents } from "scmp-sdk/components/rosetta/hooks";
import type { GA4Event, TriggerPoint } from "scmp-sdk/lib/google-analytics/ga4-types";

import { ResetAfterSuccessTimeout } from "./consts";
import type { ContextValue } from "./contexts";
import { NewsletterContextProvider } from "./contexts";
import { DisplayComponent, Variant } from "./enums";
import { useSignedUpNewsletter } from "./hooks";
import { getTriggerPoint } from "./sign-up-newsletter/helpers";
import {
  Container,
  FadeContainer,
  StyledFindOutMore,
  StyledSignUpNewsletter,
  StyledSignUpSuccessMessage,
} from "./styles";
import type { DisplayComponentRenderFunctionMap, NewsletterHook, ThemeConfig } from "./types";
import {
  AcquisitionNewsletterPopupTheme,
  acquisitionNewsletterPopupThemeConfig,
  ArticleDarkTheme,
  ArticleTheme,
  FooterTheme,
  LiveArticleTheme,
  NewsletterArticleBottomTheme,
  newsletterArticleBottomThemeConfig,
  NewsletterArticleTheme,
  newsletterArticleThemeConfig,
  NewsletterListTheme,
  newsletterListThemeConfig,
  PostiesTheme,
  useAcquisitionNewsletterPopupRender,
  useArticleRender,
} from "./variants";
import { Article20Theme, useArticle20Render } from "./variants/article-20-theme";
import { HomepageTheme } from "./variants/homepage-theme";
import { useHomepageRender } from "./variants/homepage-theme/hooks";

const defaultThemeConfig: ThemeConfig = {
  defaultDisplayComponent: DisplayComponent.FindOutMore,
  hasResetAfterSuccess: true,
  hasSanitizeSubscribedNewsletter: true,
};

export type Props = ContextValue &
  React.DetailsHTMLAttributes<HTMLDivElement> & {
    children?: ReactNode;
    /**
     * Will hide the newsletter when all the newsletters has been subscribed
     */
    hideWhenNoNewsletters?: boolean;
    isToggleSuccessMessage?: boolean;
    multipleNewsletters?: boolean;
    onSuccess?: () => void;
    trigger_point?: TriggerPoint;
  };

type AcquisitionNewsletterPopup = Props & {
  campaignName: string;
  variant: Variant.AcquisitionNewsletterPopup;
};

export const NewsletterWidget: React.FunctionComponent<AcquisitionNewsletterPopup | Props> = ({
  campaignName,
  children,
  hideWhenNoNewsletters = false,
  isToggleSuccessMessage = false,
  multipleNewsletters = false,
  newsletterItems,
  onSuccess,
  trigger_point,
  variant,
  ...props
}) => {
  const renderMap: Record<
    Variant,
    {
      config?: ThemeConfig;
      hook?: NewsletterHook;
      renderComponent: React.FunctionComponent;
    }
  > = useMemo(
    () => ({
      [Variant.AcquisitionNewsletterPopup]: {
        config: acquisitionNewsletterPopupThemeConfig,
        hook: useAcquisitionNewsletterPopupRender,
        renderComponent: AcquisitionNewsletterPopupTheme,
      },
      [Variant.Article]: {
        hook: useArticleRender,
        renderComponent: ArticleTheme,
      },
      [Variant.Article20]: {
        hook: useArticle20Render,
        renderComponent: Article20Theme,
      },
      [Variant.ArticleDark]: {
        hook: useArticleRender,
        renderComponent: ArticleDarkTheme,
      },
      [Variant.Footer]: {
        renderComponent: FooterTheme,
      },
      [Variant.Homepage]: {
        hook: useHomepageRender,
        renderComponent: HomepageTheme,
      },
      [Variant.LiveArticle]: {
        renderComponent: LiveArticleTheme,
      },
      [Variant.NewsletterArticle]: {
        config: newsletterArticleThemeConfig,
        renderComponent: NewsletterArticleTheme,
      },
      [Variant.NewsletterArticleBottom]: {
        config: newsletterArticleBottomThemeConfig,
        renderComponent: NewsletterArticleBottomTheme,
      },
      [Variant.NewsletterList]: {
        config: newsletterListThemeConfig,
        renderComponent: NewsletterListTheme,
      },
      [Variant.NewsletterPreview]: {
        config: newsletterListThemeConfig,
        renderComponent: NewsletterListTheme,
      },
      [Variant.Posties]: {
        renderComponent: PostiesTheme,
      },
      [Variant.Section]: {
        hook: useHomepageRender,
        renderComponent: HomepageTheme,
      },
    }),
    [],
  );

  const Component = useMemo(() => renderMap[variant].renderComponent, [renderMap, variant]);
  const themeConfig = useMemo(
    () => renderMap[variant].config ?? defaultThemeConfig,
    [renderMap, variant],
  );

  const [isResetAfterSuccess, setIsResetAfterSuccess] = useState(false);
  const handleOnSuccess = useCallback(() => {
    setDisplayComponent(DisplayComponent.SignUpSuccessMessage);
    if (themeConfig.hasResetAfterSuccess) setIsResetAfterSuccess(true);
    onSuccess?.();
  }, [onSuccess, themeConfig]);

  const displayComponentRenderFunctionMap: DisplayComponentRenderFunctionMap = {
    [DisplayComponent.FindOutMore]: p => <StyledFindOutMore {...p} />,
    [DisplayComponent.SignUpNewsletter]: p => (
      <StyledSignUpNewsletter onSuccess={handleOnSuccess} {...p} />
    ),
    [DisplayComponent.SignUpSuccessMessage]: p => <StyledSignUpSuccessMessage {...p} />,
  };
  useEffect(() => {
    if (!isResetAfterSuccess) return;
    const timer = setTimeout(() => setIsResetAfterSuccess(false), ResetAfterSuccessTimeout);
    return () => clearTimeout(timer);
  }, [isResetAfterSuccess, themeConfig.hasResetAfterSuccess]);

  const newsletterHook = renderMap[variant].hook;
  const hookRenderFunctionMap = newsletterHook?.(handleOnSuccess) ?? {};
  const displayComponentMap = Object.entries({
    ...displayComponentRenderFunctionMap,
    ...hookRenderFunctionMap,
  }).reduce(
    (accumulator, [key, renderFunction]) => {
      accumulator[key as DisplayComponent] = renderFunction();
      return accumulator;
    },
    {} as Record<DisplayComponent, JSX.Element>,
  );

  const signedUpNewsletterItemIds = useSignedUpNewsletter();
  const sanitizedNewsletterItems = useDeepCompareMemo(() => {
    if (!themeConfig.hasSanitizeSubscribedNewsletter) return newsletterItems;
    return newsletterItems.filter(
      newsletter => !signedUpNewsletterItemIds.includes(newsletter.entityId),
    );
  }, [newsletterItems, signedUpNewsletterItemIds, themeConfig.hasSanitizeSubscribedNewsletter]);

  const newsletterItemForDisplay = useMemo(
    () => first(sanitizedNewsletterItems),
    [sanitizedNewsletterItems],
  );

  const ga4ImpressionEvent = useCallback<() => GA4Event>(
    () => ({
      action: "imp",
      category: "nl",
      customized_parameters: {
        internal_campaign: campaignName,
        newsletter_name: newsletterItemForDisplay?.name,
        trigger_point: trigger_point ?? getTriggerPoint(variant),
      },
      subcategory: "sign_up",
    }),
    [newsletterItemForDisplay, trigger_point, variant, campaignName],
  );

  const { captureTrackImpressionEventTargetElement } = useSendImpressionEvents({
    ga4Event: ga4ImpressionEvent,
  });

  const [displayComponent, setDisplayComponent] = useState(themeConfig.defaultDisplayComponent);
  const resetDisplayComponent = useCallback(() => {
    setDisplayComponent(
      sanitizedNewsletterItems.length > 0
        ? DisplayComponent.SignUpNewsletter
        : themeConfig.defaultDisplayComponent,
    );
  }, [sanitizedNewsletterItems, themeConfig.defaultDisplayComponent]);
  const latestResetDisplayComponent = useLatest(resetDisplayComponent);

  useEffect(() => {
    if (isResetAfterSuccess) return;
    latestResetDisplayComponent.current();
  }, [isResetAfterSuccess, latestResetDisplayComponent]);

  useEffect(() => {
    if (isToggleSuccessMessage) {
      setDisplayComponent(DisplayComponent.SignUpSuccessMessage);
    } else {
      latestResetDisplayComponent.current();
    }
  }, [isToggleSuccessMessage, latestResetDisplayComponent]);

  // Get context value from props
  const {
    dataMatrix,
    findOutMoreButtonDisplayComponent,
    queryParametersForSocialButtons,
    signUpButtonDisplayComponent,
    summaryComponent,
    titleComponent,
  } = props;

  if (hideWhenNoNewsletters && sanitizedNewsletterItems.length === 0) return null;

  return (
    <NewsletterContextProvider
      multipleNewsletters={multipleNewsletters}
      value={{
        campaignName,
        dataMatrix,
        findOutMoreButtonDisplayComponent,
        newsletterItems: sanitizedNewsletterItems,
        queryParametersForSocialButtons,
        signUpButtonDisplayComponent,
        summaryComponent,
        titleComponent,
        variant,
      }}
    >
      {children}
      <Component {...props}>
        <Container ref={captureTrackImpressionEventTargetElement}>
          <SwitchTransition mode="out-in">
            <Fade appear={false} key={displayComponent} timeout={500}>
              <FadeContainer>
                {Object.entries(displayComponentMap).map(([key, component]) => (
                  <React.Fragment key={key}>
                    {(key as DisplayComponent) === displayComponent && component}
                  </React.Fragment>
                ))}
              </FadeContainer>
            </Fade>
          </SwitchTransition>
        </Container>
      </Component>
    </NewsletterContextProvider>
  );
};

NewsletterWidget.displayName = "NewsletterWidget";
